import { Injectable, OnDestroy } from '@angular/core';
import { ReportPageOrientation } from '../../../models/reports/ReportPageOrientation';
import { ReportList } from '../../../models/dtos/Enums/ReportList';
import { CONSTANTS } from '../../../Constants/Constants';
import { IReportData } from '../../../models/reports/IReportData';
import { ITableData } from '../../../models/reports/ITableData';
import { TranslateService } from '@ngx-translate/core';
import { Material, MaterialData, SQWebArea, SQWebController, SQWebDesigoOptic, SQWebPanel, SQWebSystem, SubSystem, TypicalOfSystemHelper } from '../../../models/dtos';
import { IndexDBService } from '../../common/indexDB.service';
import { IDataItem } from '../../../models/reports/IDataItem';
import { ReportTextAlignment } from '../../../models/reports/ReportTextAlignment';
import { SQWebAreaChildren } from '../../../models/SQWebAreaChildren';
import { SQWebAreaChildrenBase } from '../../../models/SQWebAreaChildrenBase';
import { SQWebSystemChildren } from '../../../models/SQWebSystemChildren';
import { HierarchyContext } from '../../../models/reports/HierarchyContext';
import { MaterialRowData } from '../../../models/reports/MaterialRowData';
import { ReportsCommonService } from '../reports-common/reports-common.service';
import { CommonDataService } from '../../common-data.service';
import { MaterialIndicators } from '../../../models/MaterialIndicators';

// Column position constants for better maintainability
const MATERIAL_BY_AREA_COLUMN_POSITIONS = {
  EXPANDED: {
    MATERIAL_REFERENCE: 0,
    MODEL_NUMBER: 1,
    MANUFACTURER_PART: 2,
    TYPICAL_OF: 3,
    DESCRIPTION: 4,
    QTY: 5,
    TOTAL_QTY: 6,
    TOTAL_COLUMNS: 7
  },
  COLLAPSED: {
    MATERIAL_REFERENCE: 0,
    TYPICAL_OF: 1,
    DESCRIPTION: 2,
    QTY: 3,
    TOTAL_QTY: 4,
    TOTAL_COLUMNS: 5
  },
  WITH_NOTES: {
    TOTAL_QTY: 0,
    MATERIAL_REFERENCE: 1,
    DESCRIPTION: 2,
    NOTES: 3,
    TOTAL_COLUMNS: 4
  }
};

@Injectable()
export class SolMaterialByAreaService implements OnDestroy {
  private materialByAreaInfoExpandedList: ITableData[] = [];
  private materialByAreaInfoList: ITableData[] = [];
  private materialWithNotesByAreaInfoList: ITableData[] = [];
  private hierarchyMaterialMaps: Map<string, Map<string, MaterialRowData>> = new Map();
  private notesHierarchyMaterialMaps: Map<string, Map<string, MaterialRowData>> = new Map();
  /** Tracks which typical systems have been processed (first occurrence kept) per area: Map<areaId, Set<typicalOfHelperId>> */
  private processedTypicalSystemIdsPerArea: Map<string, Set<string>> = new Map();
  isReportGenerated: boolean = false;

  constructor(
    private readonly commonDataService: CommonDataService,
    private readonly reportsCommonService: ReportsCommonService,
    private readonly translate: TranslateService,
    private readonly indexDBService: IndexDBService
  ) { }

  async generateReport(reportId: ReportList, pageOrientation: ReportPageOrientation = CONSTANTS.REPORT_PAGEORIENTATION_PORTRAIT, internalReport: boolean = true): Promise<void> {
    const reportName = this.reportsCommonService.getSelectedReportName();
    const reportHeaderData = this.commonDataService.getReportHeaderData();
    const reportData: IReportData = {
      selectionName: this.translate.instant(reportName),
      reportTitle: this.translate.instant(reportName),
      reportTitles: {
        data: this.reportsCommonService.generateReportTitles(reportHeaderData)
      },
      tableData: await this.generateTableData(reportId),
      pageOrientation: pageOrientation,
      internalReport: internalReport
    };

    this.reportsCommonService.setSelectedReport(reportData);
  }

  private async generateTableData(reportId: ReportList): Promise<ITableData[]> {
    await this.generateMaterialByAreaReport();

    let selectedReportData: ITableData[] = [];
    switch (reportId) {
      case ReportList.EstimateMaterialBreakdownViewModel:
        selectedReportData = this.reportsCommonService.getReportsCheckboxes().expanded.checked ? this.materialByAreaInfoExpandedList : this.materialByAreaInfoList;
        break;
      case ReportList.MaterialWithNotesByAreaViewModel:
        selectedReportData = this.materialWithNotesByAreaInfoList;
        break;
      default:
        break;
    }

    return selectedReportData;
  }

  private async generateMaterialByAreaReport(): Promise<void> {
    // Clear all previous data to ensure clean state - only when actually regenerating
    this.materialByAreaInfoExpandedList = [];
    this.materialByAreaInfoList = [];
    this.materialWithNotesByAreaInfoList = [];
    this.clearMemory();
    
    const areas = this.commonDataService.getSQWebProject().areas;

    for (const area of areas) {
      if (area.isItemVisible) {
        await this.processAreaForMaterialReport(area);
      }
    }
  }


  private async processAreaForMaterialReport(area: SQWebArea): Promise<void> {
    
    let tableIndex: number | undefined;
    let notesTableIndex: number | undefined;
    const areaId = area.itemUniqueId;
    
    // Process all children - can be sub-areas or systems
    for (const child of area.children) {
      const childItem = child as SQWebAreaChildren;
      if (childItem.nodeType !== CONSTANTS.NODETYPE_CONTROLLER && !childItem.isItemVisible) {
        continue;
      }
      
      if (childItem.nodeType === CONSTANTS.NODETYPE_AREA) {
        // Recursively process sub-areas
        await this.processAreaForMaterialReport(child as SQWebArea);
      } else {
        // Handle typical system logic - only process first occurrence per area
        if (childItem.nodeType === CONSTANTS.NODETYPE_BSETSYSTEM) {
          const system = child as SQWebSystem;
          if (this.shouldSkipSystem(system, area.itemUniqueId)) {
            continue;
          }
        }
        
        // Process System/Controller/Panel/DesigoOptic
        const result = await this.processSystemForMaterialReport(childItem as SQWebAreaChildrenBase, area.itemName, area.itemUniqueId, tableIndex );
        
        // Process for notes report simultaneously
        const notesResult = await this.processSystemForNotesReport( childItem as SQWebAreaChildrenBase, area.itemName, area.itemUniqueId, notesTableIndex );  

        
        // Update table indices for subsequent systems in same area
        if (result.tableIndex !== null) {
          tableIndex = result.tableIndex;
        }
        
        if (notesResult.tableIndex !== null) {
          notesTableIndex = notesResult.tableIndex;
        }
      }
    }
  }

  private shouldSkipSystem(systemItem: SQWebSystem, areaId: string): boolean {
    const typicalId = systemItem.systemTypicalOfHelperUniqueId;
    
    // No typical ID means unique system - always process
    if (!typicalId) {
      return false;
    }
    
    // Initialize per-area tracking if needed
    if (!this.processedTypicalSystemIdsPerArea.has(areaId)) {
      this.processedTypicalSystemIdsPerArea.set(areaId, new Set());
    }
    
    const areaProcessedSet = this.processedTypicalSystemIdsPerArea.get(areaId)!;
    
    // If we've already processed this typical system in THIS area, skip it
    if (areaProcessedSet.has(typicalId)) {
      return true;
    }
    
    // First occurrence in this area - mark as processed and continue
    areaProcessedSet.add(typicalId);
    return false;
  }

  /**
   * Process a single system and return table creation info
   */
  private async processSystemForMaterialReport(systemItem: SQWebAreaChildrenBase, areaName: string, areaId: string, existingTableIndex: number | undefined): Promise<{ tableIndex: number }> {
    
    // For typical systems, use the master's material data
    let materialDataGuid = systemItem.itemUniqueId;
    let typicalOfDisplay: string = CONSTANTS.EMPTY_STRING;
    let typicalOfCountInArea: number = CONSTANTS.ONE;

    if (systemItem.nodeType === CONSTANTS.NODETYPE_BSETSYSTEM) {
      const system = systemItem as SQWebSystem;
      const typicalId = system.systemTypicalOfHelperUniqueId;
      
      if (typicalId) {
        // Get total count from TypicalOfSystemHelper
        const typicalOfHelpers = this.commonDataService.getTypicalOfHelpers();
        const helper: TypicalOfSystemHelper | undefined = typicalOfHelpers[typicalId];
        const visibleSystems = helper?.typicalOfSystems?.filter(system => system.isItemVisible);
        const totalCount = visibleSystems?.length ?? CONSTANTS.ONE;
        
        const translatedInArea = this.translate.instant("IN_AREA");
        typicalOfCountInArea = visibleSystems?.filter(system => system.parentUniqueId === areaId).length || CONSTANTS.ONE;
        
        // Format: "totalCount [areaCount in Area]"
        typicalOfDisplay = `${totalCount} [ ${typicalOfCountInArea} ${translatedInArea} ]`;
        
        // Use master's ID to fetch material data
        if (helper?.typicalOfCollectionHolderId) {
          materialDataGuid = helper.typicalOfCollectionHolderId;
        }
      }
    }

    const materialData = await this.indexDBService.getMaterialDataByGuid(materialDataGuid);
    const systemChildren = materialData?.systemChildrenCollection ?? [];
    
    // Skip if materialData is null or no valid materials found
    if (!materialData || (!systemChildren.length && !this.hasValidMaterialsInSystem(materialData))) {
      return { tableIndex: existingTableIndex };
    }

    // Create area table if this is the first system in the area
    let tableIndex = existingTableIndex;
    if (tableIndex === null || tableIndex === undefined) {
      this.createMaterialByAreaTable(areaName);
      tableIndex = this.getCurrentMaterialList().length - CONSTANTS.ONE;
    }
    
    // Add system name row with proper formatting
    const systemDisplayName = systemItem.nodeType === CONSTANTS.NODETYPE_BSETSYSTEM ? `${this.translate.instant('SYSTEM')}: ${systemItem.itemName}` : systemItem.itemName;
    this.addSystemNameRow(tableIndex, systemDisplayName, typicalOfDisplay);

    // Process all materials and subsystems within this system
    const hierarchyContext: HierarchyContext = {
      areaId,
      systemId: systemItem.itemUniqueId
    };
    this.processSystemChildren(materialData, tableIndex, hierarchyContext, typicalOfCountInArea);

    return { tableIndex };
  }

  
  private isValidMaterial(item: Material): boolean {
    const quantity = item.quantity ?? 0;
    const cost = item.cost ?? 0;
    return quantity > 0 && cost > 0;
  }

  private isValidMaterialForNotes(item: Material): boolean {
    return this.isValidMaterial(item) ||
      (item.referenceNumber === CONSTANTS.USER_DEFINED_REFERENCE_NUMBER && (item.quantity ?? 0) > 0);
  }

  private hasValidMaterialsInSystem(materialData: MaterialData): boolean {
    const systemChildren = materialData.systemChildrenCollection ?? [];
    return systemChildren.some(item => {
      const childItem = item as SQWebSystemChildren;
      
      // Check if it's a subsystem with children, or a valid material
      if (childItem.systemComponentNodeType === CONSTANTS.NODETYPE_SUBSYSTEM) {
        const subSystem = childItem as SubSystem;
        return subSystem.children?.some((child) => this.isValidMaterial(child as Material)) ?? false;
      }
      
      return this.isValidMaterial(childItem as Material);
    });
  }

  private processSystemChildren(
    materialData: MaterialData, 
    tableIndex: number, 
    hierarchyContext: HierarchyContext, 
    typicalOfCountInArea: number): void {
    let systemChildrenCollection = materialData.systemChildrenCollection ?? [];
    for (const child of systemChildrenCollection) {
      const subSystem = child as SQWebSystemChildren;
      
      if (subSystem.systemComponentNodeType === CONSTANTS.NODETYPE_SUBSYSTEM) {
        const subSystemTyped = subSystem as SubSystem;
        // Process subsystem
        this.processSubSystem(subSystemTyped, tableIndex, hierarchyContext, typicalOfCountInArea);
      } else {
        // Process material (hierarchy 1) - system level
        this.processMaterialItemCommon(child as Material, tableIndex, 1, hierarchyContext, 'MATERIAL_BY_AREA', typicalOfCountInArea);
      }
    }
  }

  private processSubSystem(
    subSystem: SubSystem, 
    tableIndex: number, 
    hierarchyContext: HierarchyContext, 
    typicalOfCountInArea: number): void {

    const subSystemQty = subSystem.quantity || 0;
    
    if (subSystemQty === 0 || !subSystem.children) {
      return;
    }

    // Check if subsystem has valid materials
    const hasValidMaterials = subSystem.children.some((child) => this.isValidMaterial(child as Material));

    if (!hasValidMaterials) {
      return;
    }

    // Add subsystem name row ONLY in expanded mode
    // In collapsed mode, materials are consolidated across subsystems, so no subsystem headers
    const reportCheckboxes = this.reportsCommonService.getReportsCheckboxes();
    if (reportCheckboxes.expanded.checked) {
      const subSystemName = subSystem.itemName ;
      this.addSubSystemNameRow(tableIndex, subSystemName, subSystemQty);
    }

    // Process subsystem materials with subsystem context
    const subSystemId = subSystem.referenceNumber;
    const subSystemContext: HierarchyContext = {
      ...hierarchyContext,
      subSystemRefNumber: subSystemId
    };
    
    // Level 2 for expanded (under subsystem), Level 1 for collapsed (under system)
    const expandedCheckboxes = this.reportsCommonService.getReportsCheckboxes();
    const hierarchyLevel = expandedCheckboxes.expanded.checked ? CONSTANTS.TWO : CONSTANTS.ONE; 
    
    for (const materialItem of subSystem.children) {
      this.processMaterialItemCommon(materialItem as Material, tableIndex, hierarchyLevel, subSystemContext, 'MATERIAL_BY_AREA', typicalOfCountInArea);
    }
  }

  private processMaterialItemCommon(
    materialItem: Material, 
    tableIndex: number, 
    hierarchyLevel: number, 
    hierarchyContext: HierarchyContext, 
    reportType: 'MATERIAL_BY_AREA' | 'MATERIAL_WITH_NOTES_BY_AREA', 
    typicalOfCountInArea: number = CONSTANTS.ONE ): void {

    const quantity = materialItem.quantity || 0;
    const cost = materialItem.cost || 0;
    
    if (reportType === 'MATERIAL_WITH_NOTES_BY_AREA'
      ? !this.isValidMaterialForNotes(materialItem)
      : quantity === 0 || cost === 0) {
      return;
    }

    // Cache frequently used values
    const hierarchyKey = this.createHierarchyKey(hierarchyContext);
    const materialKey = reportType === 'MATERIAL_BY_AREA' 
      ? this.createCompositeMaterialKey(materialItem)
      : this.createNotesCompositeMaterialKey(materialItem);
    
    // Get the appropriate maps and lists based on report type
    const hierarchyMaps = reportType === 'MATERIAL_BY_AREA' ? this.hierarchyMaterialMaps : this.notesHierarchyMaterialMaps;
    
    const targetList = reportType === 'MATERIAL_BY_AREA' ? this.getCurrentMaterialList() : this.materialWithNotesByAreaInfoList;

    if (!hierarchyMaps.has(hierarchyKey)) {
      hierarchyMaps.set(hierarchyKey, new Map<string, MaterialRowData>());
    }
    
    const currentHierarchyMap = hierarchyMaps.get(hierarchyKey)!;
    const existingMaterial = currentHierarchyMap.get(materialKey);

    if (existingMaterial) {
      // Material exists at this exact hierarchy level - increment occurrence count
      
      // Verify the row still exists at the expected index
      const isValidRow = reportType === 'MATERIAL_BY_AREA' ? this.isValidRowIndex(tableIndex, existingMaterial.rowIndex) : this.isValidNotesRowIndex(tableIndex, existingMaterial.rowIndex);
        
      if (isValidRow) {
        // Update the quantity in the existing row to show occurrence count
        if (reportType === 'MATERIAL_BY_AREA') {
          this.updateMaterialQuantity(tableIndex, existingMaterial.rowIndex, 1, typicalOfCountInArea);
        } else {
          this.updateNotesRowQuantity(tableIndex, existingMaterial.rowIndex, 1, typicalOfCountInArea);
        }
        existingMaterial.quantity += 1;
      } else {
        // Row index is stale, create new row with occurrence count of 1
        if (reportType === 'MATERIAL_BY_AREA') {
          this.addMaterialItemRow(tableIndex, materialItem, hierarchyLevel, 1, typicalOfCountInArea);
        } else {
          this.addMaterialItemRowForNotes(tableIndex, materialItem, hierarchyLevel, 1, typicalOfCountInArea);
        }
        const newRowIndex = targetList[tableIndex].data.length - 1;
        currentHierarchyMap.set(materialKey, { rowIndex: newRowIndex, quantity: existingMaterial.quantity + 1 });
      }
    } else {
      // New material at this hierarchy level - add row with occurrence count of 1
      if (reportType === 'MATERIAL_BY_AREA') {
        this.addMaterialItemRow(tableIndex, materialItem, hierarchyLevel, 1, typicalOfCountInArea);
      } else {
        this.addMaterialItemRowForNotes(tableIndex, materialItem, hierarchyLevel, 1, typicalOfCountInArea);
      }
      
      // Track this material for consolidation within this specific hierarchy
      const rowIndex = targetList[tableIndex].data.length - CONSTANTS.ONE;
      currentHierarchyMap.set(materialKey, { rowIndex, quantity: 1 });
    }
  }

  private isValidRowIndex(tableIndex: number, rowIndex: number): boolean {
    const tableList = this.getCurrentMaterialList();
    const table = tableList[tableIndex];
    return table && table.data && rowIndex >= 0 && rowIndex < table.data.length;
  }

  private createHierarchyKey(context: HierarchyContext): string {
    
    // Create unique key for each hierarchy level (Area > System > SubSystem)
    const parts = [context.areaId, context.systemId];
    
    if (context.subSystemRefNumber) {
      parts.push(context.subSystemRefNumber);
    }
    
    const key = parts.join('::');
    return key;

    // TODO: Check later if this logic is needed
    // if (this.reportsCommonService.getIsReportExpandedChecked()) {
    //   // Expanded mode: Full hierarchy isolation (Area > System > SubSystem)
    //   // Materials are kept separate between different subsystems
    //   if (context.subSystemRefNumber) {
    //     parts.push(context.subSystemRefNumber);
    //   }
    // }
    // // Collapsed mode: Only Area > System level isolation (no SubSystem isolation)
    // // This allows materials to be consolidated across subsystems within same system
  }

  private getCurrentMaterialList(): ITableData[] {
    const checkboxes = this.reportsCommonService.getReportsCheckboxes();
    return checkboxes.expanded.checked ? this.materialByAreaInfoExpandedList : this.materialByAreaInfoList;
  }

  private isGenericNameApplicable(materialItem: Material): void {
    if(this.reportsCommonService.isGenericNameApplicable()) {
      materialItem.description = this.reportsCommonService.getGenericNameBySSN(materialItem.ssn) || materialItem.description;
    }
  }

  /**
   * Creates composite material key for consolidation within same hierarchy level
   * Expanded mode: Reference + Model + Manufacturer + Description (detailed segregation)
   * Collapsed mode: Reference + Description (simplified segregation)
   * @param materialItem - Material to create key for
   * @returns Unique string key for material consolidation
   */
  private createCompositeMaterialKey(materialItem: Material): string {

    this.isGenericNameApplicable(materialItem);

    const referenceNumber = materialItem.referenceNumber;
    const description = materialItem.description;
    
    const checkboxes = this.reportsCommonService.getReportsCheckboxes();
    const isExpanded = checkboxes.expanded.checked;
    
    if (isExpanded) {
      // Expanded mode: Full segregation with all 4 keys - more detailed grouping
      const modelNumber = materialItem.mpn;
      const manufacturer = materialItem.manufacturerName;
      return `${referenceNumber}::${modelNumber}::${manufacturer}::${description}`;
    } else {
      // Collapsed mode: Simplified segregation with only 2 keys - broader grouping
      return `${referenceNumber}::${description}`;
    }
  }

  private updateMaterialQuantity(
    tableIndex: number, 
    rowIndex: number, 
    additionalQuantity: number, 
    typicalOfCountInArea: number): void {

    const currentMaterialList = this.getCurrentMaterialList();
    const table = currentMaterialList[tableIndex];
    if (!table || !table.data || rowIndex >= table.data.length) {
      return;
    }
    
    const row = table.data[rowIndex];
    if(!row){
      return;
    }
    
    const checkboxes = this.reportsCommonService.getReportsCheckboxes();
    const isExpanded = checkboxes.expanded.checked;
    const columnPositions = isExpanded ? MATERIAL_BY_AREA_COLUMN_POSITIONS.EXPANDED : MATERIAL_BY_AREA_COLUMN_POSITIONS.COLLAPSED;
    
    if (row && row.data && row.data.length >= columnPositions.TOTAL_COLUMNS) {
      // Update QTY column (occurrence count)
      const currentQty = parseInt(row.data[columnPositions.QTY].data) || 0;
      const newQty = currentQty + additionalQuantity;
      
      row.data[columnPositions.QTY].data = newQty.toString();
      // Total QTY = QTY * typicalOfCountInArea (accounts for typical-of multiplier)
      row.data[columnPositions.TOTAL_QTY].data = (newQty * typicalOfCountInArea).toString();
    }
  }

  private createMaterialByAreaTable(areaName: string): void {
    const title = this.createTitleHeaderRow();
    const tableHeader = this.reportsCommonService.generateReportTableHeader(`${this.translate.instant('AREA_LEVEL')}: ${areaName}`);
    tableHeader.forEach(headerRow => {
      headerRow.headers.forEach(header => {
        header.columnspan = title.length;
      });
    });

    const tableData: ITableData = {
      tableHeader,
      title,
      data: [],
      tableFooter: [],
      isTableBordered: true,
      isPageBreakAfter: false
    };
    
    this.getCurrentMaterialList().push(tableData);
  }

  private createTitleHeaderRow(): IDataItem[] {
    const checkboxes = this.reportsCommonService.getReportsCheckboxes();
    const isExpanded = checkboxes.expanded.checked;
    
    if (isExpanded) {
      return [
        { data: this.translate.instant('MATERIAL_REFERENCE_#'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left", width: "150" },
        { data: this.translate.instant('MODEL_#'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left", width: "80" },
        { data: this.translate.instant('MANUFACTURER_PART_#'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left", width: "130" },
        { data: this.translate.instant('TYPICAL_OF'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center", width: "70" },
        { data: this.translate.instant('DESCRIPTION'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left" },
        { data: this.translate.instant('Quantity'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center", width: "35" },
        { data: this.translate.instant('TOTAL_QTY'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center", width: "35" }
      ];
    } else {
      // Collapsed mode: 5 columns
      return [
        { data: this.translate.instant('MATERIAL_REFERENCE_#'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left", width: "150" },
        { data: this.translate.instant('TYPICAL_OF'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center", width: "70" },
        { data: this.translate.instant('DESCRIPTION'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "left" },
        { data: this.translate.instant('Quantity'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center", width: "35" },
        { data: this.translate.instant('TOTAL_QTY'), columnspan: CONSTANTS.ONE, hierarchyLevel: CONSTANTS.ZERO, alignment: "center", width: "35" }
      ];
    }
  }

  private addSystemNameRow(tableIndex: number, systemName: string, typicalOfId: string = ''): void {
    const emptyCell = this.createEmptyCell();
    const emptyCenterCell = this.createEmptyCenterCell();
    const systemCell = this.createCell(systemName, CONSTANTS.ZERO, 'left');
    systemCell.textColor = CONSTANTS.REPORT_DARK_BLUE_COLOR;
    
    const typicalOfCell = this.createCell(typicalOfId || '', CONSTANTS.ZERO, 'center');
    
    let rowData: IDataItem[];
    const checkboxes = this.reportsCommonService.getReportsCheckboxes();
    if (checkboxes.expanded.checked) {
      rowData = [
        systemCell,                                    // MATERIAL_REFERENCE
        emptyCell,                                     // MODEL_NUMBER  
        emptyCell,                                     // MANUFACTURER_PART
        typicalOfCell,                                 // TYPICAL_OF
        emptyCell,                                     // DESCRIPTION
        emptyCenterCell,                               // QTY
        emptyCenterCell                                // TOTAL_QTY
      ];
    } else {
      rowData = [
        systemCell,                                    // MATERIAL_REFERENCE
        typicalOfCell,                                 // TYPICAL_OF
        emptyCell,                                     // DESCRIPTION
        emptyCenterCell,                               // QTY
        emptyCenterCell                                // TOTAL_QTY
      ];
    }
    const currentMaterialList = this.getCurrentMaterialList();
    currentMaterialList[tableIndex].data.push({ data: rowData });
  }

  private addSubSystemNameRow(tableIndex: number, subSystemName: string, quantity: number): void {
    const emptyCell = this.createEmptyCell();
    const emptyCenterCell = this.createEmptyCenterCell();
    const subSystemCell = this.createCell(this.translate.instant('SUBSYSTEM') + ': ' + subSystemName, CONSTANTS.ONE, 'left');
    subSystemCell.textColor = CONSTANTS.REPORT_DARK_BLUE_COLOR;
    
    const quantityCell = this.createCell(quantity.toString(), CONSTANTS.ZERO, 'center');
    
    let rowData: IDataItem[];
    
    const checkboxes = this.reportsCommonService.getReportsCheckboxes();
    if (checkboxes.expanded.checked) {
      rowData = [
        subSystemCell,                                 // MATERIAL_REFERENCE
        emptyCell,                                     // MODEL_NUMBER
        emptyCell,                                     // MANUFACTURER_PART
        emptyCenterCell,                               // TYPICAL_OF
        emptyCell,                                     // DESCRIPTION
        quantityCell,                                  // QTY
        emptyCenterCell                                // TOTAL_QTY
      ];
    } else {
      rowData = [
        subSystemCell,                                 // MATERIAL_REFERENCE
        emptyCenterCell,                               // TYPICAL_OF
        emptyCell,                                     // DESCRIPTION
        quantityCell,                                  // QTY
        emptyCenterCell                                // TOTAL_QTY
      ];
    }
    
    const currentMaterialList = this.getCurrentMaterialList();
    currentMaterialList[tableIndex].data.push({ data: rowData });
  }

  private addMaterialItemRow(
    tableIndex: number, 
    materialItem: Material, 
    hierarchyLevel: number,
    quantity: number,
    typicalOfCountInArea: number
  ): void {
    const quantityStr = quantity.toString();
    const totalQtyStr = (quantity * typicalOfCountInArea).toString();
    const emptyTypicalOfCell = this.createEmptyCenterCell();
    
    let rowData: IDataItem[];
    
    const checkboxes = this.reportsCommonService.getReportsCheckboxes();
    const materialIndicator = this.reportsCommonService.generateMaterialIndicator(materialItem);
    if (checkboxes.expanded.checked) {
      rowData = [
        this.createCell(materialItem.referenceNumber || '', hierarchyLevel, 'left', materialIndicator),      // MATERIAL_REFERENCE
        this.createCell(materialItem.modelNumber || '', CONSTANTS.ZERO, 'left'),         // MODEL_NUMBER
        this.createCell(materialItem.mpn || '', CONSTANTS.ZERO, 'left'),                 // MANUFACTURER_PART
        emptyTypicalOfCell,                                                               // TYPICAL_OF
        this.createCell(materialItem.description || '', CONSTANTS.ZERO, 'left'),         // DESCRIPTION
        this.createCell(quantityStr, CONSTANTS.ZERO, 'center'),                          // QTY
        this.createCell(totalQtyStr, CONSTANTS.ZERO, 'center')                           // TOTAL_QTY
      ];
    } else {
      rowData = [
        this.createCell(materialItem.referenceNumber || '', hierarchyLevel, 'left', materialIndicator),      // MATERIAL_REFERENCE
        emptyTypicalOfCell,                                                               // TYPICAL_OF
        this.createCell(materialItem.description || '', CONSTANTS.ZERO, 'left'),         // DESCRIPTION
        this.createCell(quantityStr, CONSTANTS.ZERO, 'center'),                          // QTY
        this.createCell(totalQtyStr, CONSTANTS.ZERO, 'center')                           // TOTAL_QTY
      ];
    }
    
    const currentMaterialList = this.getCurrentMaterialList();
    currentMaterialList[tableIndex].data.push({ data: rowData });
  }

  private createCell(data: string, hierarchyLevel: number, alignment: ReportTextAlignment, materialIndicator?: MaterialIndicators): IDataItem {
    return {
      data,
      columnspan: CONSTANTS.ONE,
      hierarchyLevel,
      alignment,
      materialIndicator
    };
  }

  private createEmptyCell(): IDataItem {
    return this.createCell('', CONSTANTS.ZERO, 'left');
  }

  private createEmptyCenterCell(): IDataItem {
    return this.createCell('', CONSTANTS.ZERO, 'center');
  }

  // ========== MATERIAL WITH NOTES REPORT METHODS ==========
  // These methods handle the notes-based report generation
  // Uses different consolidation keys (includes notes field)
  

  private async processSystemForNotesReport(
    systemItem: SQWebSystem | SQWebController | SQWebPanel | SQWebDesigoOptic,
    areaName: string,
    areaId: string,
    existingTableIndex: number | undefined
  ): Promise<{ tableIndex: number }> {

    // For typical systems, use the master's material data
    let materialDataGuid = systemItem.itemUniqueId;
    let typicalOfDisplay: string = CONSTANTS.EMPTY_STRING;
    let typicalOfCountInArea = CONSTANTS.ONE;

    if (systemItem.nodeType === CONSTANTS.NODETYPE_BSETSYSTEM) {
      const system = systemItem as SQWebSystem;
      const typicalId = system.systemTypicalOfHelperUniqueId;
      
      if (typicalId) {
        const typicalOfHelpers = this.commonDataService.getTypicalOfHelpers();
        const helper: TypicalOfSystemHelper | undefined = typicalOfHelpers[typicalId];
        const visibleSystems = helper?.typicalOfSystems?.filter(system => system.isItemVisible);
        const totalCount = visibleSystems?.length ?? CONSTANTS.ONE;
        
        const translatedInArea = this.translate.instant("IN_AREA");
        typicalOfCountInArea = visibleSystems?.filter(system => system.parentUniqueId === areaId).length || CONSTANTS.ONE;
        
        typicalOfDisplay = `${totalCount} [ ${typicalOfCountInArea} ${translatedInArea} ]`;
        
        if (helper?.typicalOfCollectionHolderId) {
          materialDataGuid = helper.typicalOfCollectionHolderId;
        }
      }
    }

    const materialData = await this.indexDBService.getMaterialDataByGuid(materialDataGuid);
    const systemChildren = materialData?.systemChildrenCollection ?? [];
    
    if (!materialData || (!systemChildren.length && !this.hasValidMaterialsInSystem(materialData))) {
      return { tableIndex: existingTableIndex };
    }

    // Create table if needed
    let tableIndex = existingTableIndex;
    if (tableIndex === null || tableIndex === undefined) {
      this.createMaterialWithNotesByAreaTable(areaName);
      tableIndex = this.materialWithNotesByAreaInfoList.length - CONSTANTS.ONE;
    }

    // Add system name row
    const systemDisplayName = systemItem.nodeType === CONSTANTS.NODETYPE_BSETSYSTEM ? `${this.translate.instant('SYSTEM')}: ${systemItem.itemName}` : systemItem.itemName;
    this.addSystemNameRowForNotes(tableIndex, systemDisplayName, typicalOfDisplay);

    // Process all children (materials and subsystems)
    const hierarchyContext: HierarchyContext = {
      areaId,
      systemId: systemItem.itemUniqueId
    };

    this.processSystemChildrenForNotes(materialData, tableIndex, hierarchyContext, typicalOfCountInArea);

    return { tableIndex };
  }

  private processSystemChildrenForNotes(materialData: MaterialData, tableIndex: number, hierarchyContext: HierarchyContext, typicalOfCountInArea: number = 1): void {
    for (const child of materialData.systemChildrenCollection ?? []) {
      const subSystem = child as SQWebSystemChildren;
      
      if (subSystem.systemComponentNodeType === CONSTANTS.NODETYPE_SUBSYSTEM) {
        const subSystemTyped = subSystem as SubSystem;
        this.processSubSystemForNotes(subSystemTyped, tableIndex, hierarchyContext, typicalOfCountInArea);
      } else {
        // Process material (hierarchy 1) - system level
        this.processMaterialItemCommon(child as Material, tableIndex, 1, hierarchyContext, 'MATERIAL_WITH_NOTES_BY_AREA', typicalOfCountInArea);
      }
    }
  }

  private processSubSystemForNotes(subSystem: SubSystem, tableIndex: number, hierarchyContext: HierarchyContext, typicalOfCountInArea: number = 1): void {
    const subSystemQty = subSystem.quantity || 0;
    
    if (subSystemQty <= 0) {
      return;
    }

    // Add subsystem name row
    const subSystemName = subSystem.itemName || subSystem.description || 'Unnamed';
    this.addSubSystemNameRowForNotes(tableIndex, subSystemName, subSystemQty);

    // Process subsystem materials with subsystem context
    const subSystemId = subSystem.referenceNumber;
    const subSystemContext: HierarchyContext = {
      ...hierarchyContext,
      subSystemRefNumber: subSystemId
    };
    
    for (const materialItem of subSystem.children ?? []) {
      this.processMaterialItemCommon(materialItem as Material, tableIndex, 1, subSystemContext, 'MATERIAL_WITH_NOTES_BY_AREA', typicalOfCountInArea);
    }
  }

  private createNotesCompositeMaterialKey(materialItem: Material): string {

    this.isGenericNameApplicable(materialItem);

    const referenceNumber = materialItem.referenceNumber || 'NO_REF';
    const description = materialItem.description || 'NO_DESC';
    const notes = materialItem.notes || 'NO_NOTES';
    return `${referenceNumber}::${description}::${notes}`;
  }

  private isValidNotesRowIndex(tableIndex: number, rowIndex: number): boolean {
    const table = this.materialWithNotesByAreaInfoList[tableIndex];
    return table && table.data && rowIndex >= 0 && rowIndex < table.data.length;
  }

  private updateNotesRowQuantity(
    tableIndex: number, 
    rowIndex: number, 
    additionalQuantity: number, 
    typicalOfCountInArea: number
  ): void {
    const table = this.materialWithNotesByAreaInfoList[tableIndex];
    if (!table || !table.data || rowIndex >= table.data.length) {
      return;
    }
    
    const row = table.data[rowIndex];
    
    if (row && row.data && row.data.length >= MATERIAL_BY_AREA_COLUMN_POSITIONS.WITH_NOTES.TOTAL_COLUMNS) {
      // Reverse-calculate the base qty from the current displayed total
      const currentTotal = parseInt(row.data[MATERIAL_BY_AREA_COLUMN_POSITIONS.WITH_NOTES.TOTAL_QTY].data) || 0;
      const currentBaseQty = typicalOfCountInArea > 1 ? currentTotal / typicalOfCountInArea : currentTotal;
      const newBaseQty = currentBaseQty + additionalQuantity;
      
      // Total QTY = baseQty * typicalOfCountInArea
      row.data[MATERIAL_BY_AREA_COLUMN_POSITIONS.WITH_NOTES.TOTAL_QTY].data = (newBaseQty * typicalOfCountInArea).toString();
    }
  }

  private createMaterialWithNotesByAreaTable(areaName: string): void {
    const tableData: ITableData = {
      tableHeader: this.reportsCommonService.generateReportTableHeader(`${areaName}`),
      title: this.createNotesReportTitleHeaderRow(),
      data: [],
      tableFooter: [],
      isTableBordered: true,
      isPageBreakAfter: false
    };
    
    this.materialWithNotesByAreaInfoList.push(tableData);
  }

  private createNotesReportTitleHeaderRow(): IDataItem[] {
    return [
      { data: this.translate.instant('TOTAL_QTY'), columnspan: 1, hierarchyLevel: 0, alignment: "center", width: "60" },
      { data: this.translate.instant('MATERIAL_REFERENCE_#'), columnspan: 1, hierarchyLevel: 0, alignment: "left", width: "130" },
      { data: this.translate.instant('DESCRIPTION'), columnspan: 1, hierarchyLevel: 0, alignment: "left" },
      { data: this.translate.instant('NOTES'), columnspan: 1, hierarchyLevel: 0, alignment: "left", width: "200" }
    ];
  }

  private addSystemNameRowForNotes(tableIndex: number, systemName: string, typicalOfId: string = ''): void {
    const emptyCell = this.createEmptyCell();
    const systemCell = this.createCell(systemName, 0, 'left');
    systemCell.textColor = CONSTANTS.REPORT_DARK_BLUE_COLOR;
    systemCell.columnspan = CONSTANTS.TWO; // Span first two columns
    
    const rowData: IDataItem[] = [
      systemCell,    // TOTAL_QTY + MATERIAL_REFERENCE (colspan=2)
      emptyCell,     // DESCRIPTION
      emptyCell      // NOTES
    ];
    
    this.materialWithNotesByAreaInfoList[tableIndex].data.push({ data: rowData });
  }

  private addSubSystemNameRowForNotes(tableIndex: number, subSystemName: string, quantity: number): void {
    const emptyCell = this.createEmptyCell();
    const quantityCell = this.createCell(quantity.toString(), 0, 'center');
    const subSystemCell = this.createCell(`Subsystem: ${subSystemName}`, 0, 'left');
    
    const rowData: IDataItem[] = [
      quantityCell,     // TOTAL_QTY
      subSystemCell,    // MATERIAL_REFERENCE
      emptyCell,        // DESCRIPTION
      emptyCell         // NOTES
    ];
    
    this.materialWithNotesByAreaInfoList[tableIndex].data.push({ data: rowData });
  }

  private addMaterialItemRowForNotes( 
    tableIndex: number, 
    materialItem: Material, 
    hierarchyLevel: number, 
    quantity: number, 
    typicalOfCountInArea: number
  ): void {

    const totalQtyStr = (quantity * typicalOfCountInArea).toString();
    const notes = materialItem.notes || '';
    const materialIndicator = this.reportsCommonService.generateMaterialIndicator(materialItem);
    const rowData: IDataItem[] = [
      this.createCell(totalQtyStr, 0, 'center'),                               // TOTAL_QTY (qty * typicalOfCountInArea)
      this.createCell(materialItem.referenceNumber || '', hierarchyLevel, 'left', materialIndicator),         // MATERIAL_REFERENCE
      this.createCell(materialItem.description || '', 0, 'left'),             // DESCRIPTION
      this.createCell(notes, 0, 'left')                                      // NOTES
    ];
    
    this.materialWithNotesByAreaInfoList[tableIndex].data.push({ data: rowData });
  }

  private clearMemory(): void {
    this.hierarchyMaterialMaps.clear();
    this.notesHierarchyMaterialMaps.clear();
    this.processedTypicalSystemIdsPerArea.clear();
  }

  ngOnDestroy(): void {
    this.clearMemory();
  }
}